import React, { useEffect, useState } from "react";

import DashboardLayout from "../components/layout/DashboardLayout";

import { api } from "../lib/api";

import {
  LifeBuoy,
  Plus,
  Send,
  MessageSquare,
  Clock3,
  CheckCircle2,
} from "lucide-react";

import { toast } from "sonner";

const CATEGORIES = [
  {
    value: "account_issue",
    label: "Account Issue",
  },

  {
    value: "billing",
    label: "Billing",
  },

  {
    value: "family_vault",
    label: "Family Vault",
  },

  {
    value: "bug_report",
    label: "Bug Report",
  },

  {
    value: "feature_request",
    label: "Feature Request",
  },

  {
    value: "security",
    label: "Security",
  },

  {
    value: "other",
    label: "Other",
  },
];

export default function Support() {
  const [tickets, setTickets] = useState([]);

  const [loading, setLoading] = useState(true);

  const [creating, setCreating] = useState(false);

  const [selectedTicket, setSelectedTicket] = useState(null);

  const [messages, setMessages] = useState([]);

  const [reply, setReply] = useState("");

  const [subject, setSubject] = useState("");

  const [category, setCategory] = useState("account_issue");

  const [message, setMessage] = useState("");

  const loadTickets = async () => {
    try {
      const { data } = await api.get("/support/tickets");

      setTickets(data.tickets || []);
    } finally {
      setLoading(false);
    }
  };

  const loadTicket = async (ticketId) => {
    try {
      const { data } = await api.get(`/support/tickets/${ticketId}`);

      setSelectedTicket(data.ticket);

      setMessages(data.ticket?.messages || []);
    } catch {
      toast.error("Failed to load ticket");
    }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const createTicket = async () => {
    if (!subject || !message) {
      toast.error("Please fill all fields");
      return;
    }

    setCreating(true);

    try {
      await api.post("/support/tickets", {
        subject,
        category,
        message,
      });

      toast.success("Support ticket created");

      setSubject("");

      setMessage("");

      setCategory("account_issue");

      await loadTickets();
    } catch {
      toast.error("Failed to create ticket");
    } finally {
      setCreating(false);
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;

    try {
      await api.post(`/support/tickets/${selectedTicket.id}/reply`, {
        message: reply,
      });

      setReply("");

      await loadTicket(selectedTicket.id);

      await loadTickets();

      toast.success("Reply sent");
    } catch {
      toast.error("Failed to send reply");
    }
  };

  return (
    <DashboardLayout>
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 dark:bg-emerald-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-emerald-700 dark:text-emerald-300">
          <LifeBuoy className="h-3.5 w-3.5" />
          Support Center
        </div>

        <h1 className="mt-4 font-display text-4xl font-bold tracking-tighter text-slate-900 dark:text-white">
          Need help?
        </h1>

        <p className="mt-2 text-sm text-slate-500">
          Raise support tickets, track responses, and communicate with the
          AccessWealth support team.
        </p>
      </div>

      <div className="grid lg:grid-cols-[360px_1fr] gap-6">
        {/* LEFT SIDEBAR */}

        <div className="space-y-6">
          {/* CREATE TICKET */}

          <div className="card-soft p-6">
            <div className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-emerald-600" />

              <div className="font-display text-lg font-bold text-slate-900 dark:text-white">
                Create Ticket
              </div>
            </div>

            <div className="mt-5 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">
                  Subject
                </label>

                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="input-soft mt-2"
                  placeholder="Describe your issue"
                />
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">
                  Category
                </label>

                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="input-soft mt-2"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">
                  Message
                </label>

                <textarea
                  rows={5}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="input-soft mt-2 resize-none"
                  placeholder="Tell us more about the issue..."
                />
              </div>

              <button
                onClick={createTicket}
                disabled={creating}
                className="btn-primary w-full"
              >
                {creating ? "Creating..." : "Create Ticket"}
              </button>
            </div>
          </div>

          {/* TICKET LIST */}

          <div className="card-soft p-4">
            <div className="mb-4 px-2">
              <div className="font-display text-lg font-bold text-slate-900 dark:text-white">
                Your Tickets
              </div>

              <div className="mt-1 text-xs text-slate-500">
                Select a ticket to view conversation
              </div>
            </div>

            {loading ? (
              <div className="p-4 text-sm text-slate-500">Loading...</div>
            ) : tickets.length === 0 ? (
              <div className="p-4 text-sm text-slate-500">No tickets yet.</div>
            ) : (
              <div className="space-y-2">
                {tickets.map((ticket) => {
                  const active = selectedTicket?.id === ticket.id;

                  return (
                    <button
                      key={ticket.id}
                      onClick={() => loadTicket(ticket.id)}
                      className={`w-full rounded-2xl border p-4 text-left transition-all ${
                        active
                          ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10"
                          : "border-slate-100 dark:border-slate-800 hover:border-emerald-400"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-semibold text-slate-900 dark:text-white">
                            {ticket.subject}
                          </div>

                          <div className="mt-1 text-[11px] text-slate-500">
                            {ticket.ticket_number}
                          </div>

                          <div className="mt-3 line-clamp-2 text-xs text-slate-500">
                            {ticket.last_message || "No messages"}
                          </div>
                        </div>

                        <StatusBadge status={ticket.status} />
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT PANEL */}

        <div>
          {!selectedTicket ? (
            <div className="card-soft grid h-[700px] place-items-center p-10">
              <div className="text-center">
                <MessageSquare className="mx-auto h-12 w-12 text-slate-300" />

                <div className="mt-4 text-xl font-bold text-slate-900 dark:text-white">
                  Select a support ticket
                </div>

                <div className="mt-2 text-sm text-slate-500">
                  View full conversation history with support.
                </div>
              </div>
            </div>
          ) : (
            <div className="card-soft flex h-[700px] flex-col overflow-hidden">
              {/* HEADER */}

              <div className="border-b border-slate-100 dark:border-slate-800 p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">
                      {selectedTicket.ticket_number}
                    </div>

                    <h2 className="mt-2 font-display text-2xl font-bold text-slate-900 dark:text-white">
                      {selectedTicket.subject}
                    </h2>

                    <div className="mt-2 text-sm text-slate-500">
                      Category: {selectedTicket.category}
                    </div>
                  </div>

                  <StatusBadge status={selectedTicket.status} />
                </div>
              </div>

              {/* MESSAGES */}

              <div className="flex-1 overflow-y-auto bg-slate-50/50 dark:bg-slate-950/30 p-6">
                <div className="space-y-5">
                  {messages.map((msg) => {
                    const isAdmin = msg.sender_type === "admin";

                    return (
                      <div
                        key={msg.id}
                        className={`flex ${
                          isAdmin ? "justify-start" : "justify-end"
                        }`}
                      >
                        <div
                          className={`max-w-[75%] rounded-3xl px-5 py-4 shadow-sm ${
                            isAdmin
                              ? "border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                              : "bg-emerald-600 text-white"
                          }`}
                        >
                          {/* TOP */}

                          <div className="mb-3 flex items-center justify-between gap-4">
                            <div
                              className={`text-[10px] font-bold uppercase tracking-[0.2em] ${
                                isAdmin
                                  ? "text-blue-600 dark:text-blue-300"
                                  : "text-white/70"
                              }`}
                            >
                              {isAdmin ? "AccessWealth Support" : "You"}
                            </div>

                            <div
                              className={`text-[10px] ${
                                isAdmin ? "text-slate-400" : "text-white/70"
                              }`}
                            >
                              {msg.created_at?.slice(0, 16).replace("T", " ")}
                            </div>
                          </div>

                          {/* MESSAGE */}

                          <div className="whitespace-pre-wrap text-sm leading-7">
                            {msg.message}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* REPLY */}

              {selectedTicket.status !== "resolved" &&
                selectedTicket.status !== "closed" && (
                  <div className="border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-950 p-5">
                    <div className="rounded-3xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3">
                      <textarea
                        rows={4}
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        className="w-full resize-none bg-transparent px-2 py-2 text-sm text-slate-900 dark:text-white outline-none"
                        placeholder="Write your reply..."
                      />

                      <div className="mt-3 flex items-center justify-between border-t border-slate-200 dark:border-slate-800 pt-3">
                        <div className="text-xs text-slate-500">
                          Reply directly to support
                        </div>

                        <button
                          onClick={sendReply}
                          disabled={!reply.trim()}
                          className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition-all hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Send className="h-4 w-4" />
                          Send Reply
                        </button>
                      </div>
                    </div>
                  </div>
                )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function StatusBadge({ status }) {
  const map = {
    open: {
      label: "Open",
      className:
        "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300",
      icon: Clock3,
    },

    in_progress: {
      label: "In Progress",
      className:
        "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
      icon: Clock3,
    },

    waiting_user: {
      label: "Waiting",
      className:
        "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300",
      icon: Clock3,
    },

    resolved: {
      label: "Resolved",
      className:
        "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
      icon: CheckCircle2,
    },

    closed: {
      label: "Closed",
      className:
        "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300",
      icon: CheckCircle2,
    },
  };

  const cfg = map[status] || map.open;

  const Icon = cfg.icon;

  return (
    <div
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${cfg.className}`}
    >
      <Icon className="h-3 w-3" />

      {cfg.label}
    </div>
  );
}
